import { useEffect, useMemo } from 'react';

import { useIsCustomerSessionReady } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useChallengesQuery } from '../GraphQL/Challenges.generated';
import { useHasUnseenChallengesQuery } from '../GraphQL/HasUnseenChallenges.generated';

export const useChallenges = () => {
  const isCustomerSessionReady = useIsCustomerSessionReady();
  const [challengesResponse, fetchChallenges] = useChallengesQuery({
    requestPolicy: useChallengesNetworkPolicy(),
    pause: true,
  });
  const [hasUnseenChallengesResponse, fetchHasUnseenChallenges] =
    useHasUnseenChallengesQuery({
      requestPolicy: 'network-only',
      pause: true,
    });

  // fetch has unseen challenges state at mount and challenges change
  useEffect(() => {
    if (isCustomerSessionReady) fetchHasUnseenChallenges();
  }, [
    challengesResponse.data,
    fetchHasUnseenChallenges,
    isCustomerSessionReady,
  ]);

  const hasUnseenChallenges =
    hasUnseenChallengesResponse.data?.hasUnseenChallenges
      ?.hasUnseenChallenges ?? false;

  const challenges = challengesResponse.data?.challenges;
  const isFetchingChallenges = challengesResponse.fetching;
  const challengesError = challengesResponse.error;

  return useMemo(
    () => ({
      hasUnseenChallenges,
      challenges,
      isFetchingChallenges,
      challengesError,
      fetchChallenges,
      fetchHasUnseenChallenges,
    }),
    [
      challenges,
      challengesError,
      hasUnseenChallenges,
      isFetchingChallenges,
      fetchChallenges,
      fetchHasUnseenChallenges,
    ],
  );
};

const useChallengesNetworkPolicy = () => {
  const shouldUseCache = useFeatureFlag(
    'XP-3188-short-challenges-cache-enabled',
  );

  return shouldUseCache ? 'cache-and-network' : 'network-only';
};
